import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, Transition as _Transition, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["data-col-id", "onMouseenter"]
const _hoisted_2 = { class: "qtl-th__multi-row" }
const _hoisted_3 = ["disabled", "aria-label"]
const _hoisted_4 = ["aria-label", "disabled"]
const _hoisted_5 = {
  class: "qtl-th__sort-svg",
  width: "16",
  height: "16",
  viewBox: "0 0 16 16",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_6 = {
  key: 1,
  class: "qtl-th__col"
}
const _hoisted_7 = ["disabled", "onClick"]
const _hoisted_8 = { class: "qtl-th__col qtl-th__col--overlay" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["innerHTML"]

import MostaCheckbox from "@/components/common/basic/MostaCheckbox.vue";
import QtLiteSortIcon from "./QTLiteSortIcon.vue";
import { i18n } from "@/i18n";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import type { Directive } from "vue";
import { ColumnCategory, TableHeaderCellDataType } from "@/models/etc/types";
import type { LiteColumn } from "../types";

const LEAD_CLAMP_MAX_LINES = 3;
type LeadClampValue = { id: string; text: string };


export default /*@__PURE__*/_defineComponent({
  __name: 'QTLiteHeader',
  props: {
    columns: {},
    headerMode: {},
    headerCheckbox: {},
    WA: { type: Boolean },
    multieditAllowed: { type: Boolean },
    orderByPinned: { type: Boolean },
    showGlobalPinnedSort: { type: Boolean },
    blocked: { type: Boolean }
  },
  emits: ["headerCheckboxChange", "sort", "multieditClick", "togglePinnedOrder"],
  setup(__props: any, { emit: __emit }) {

const t = (key: string) => i18n.global.t(key);

const DT = TableHeaderCellDataType;

// Следующее состояние сортировки по клику. Порядок зависит от типа колонки:
//  • числовые: по умолчанию (1) → убыв. (3) → возр. (2) → по умолчанию (1)
//    (первым нажатием показываем от большего к меньшему);
//  • остальные типы: по умолчанию (1) → возр. (2) → убыв. (3) → по умолчанию (1).
function nextOrderOf(col: LiteColumn): number {
  const isNum = col.dataType === DT.Number;
  // из дефолта (order не 2 и не 3): числовые стартуют с убывания, остальные — с возрастания
  if (col.order !== 2 && col.order !== 3) return isNum ? 3 : 2;
  if (isNum) return col.order === 3 ? 2 : 1;
  return col.order === 2 ? 3 : 1;
}

/*
 Подсказка на значке сортировки описывает состояние, в которое перейдёт колонка ПОСЛЕ
 клика (следующий order), а не текущее. Возврат к дефолту (next === 1) одинаков для всех
 типов. Для остальных текст зависит от типа данных колонки; у даты значок такой же, как у
 логического поля, а текст подсказки отдельный.
*/
function sortTip(col: LiteColumn): string {
  const next = nextOrderOf(col);
  if (next === 1) return t("sort_tip_default");
  const asc = next === 2;
  // Колонка этапов проекта: сортировка описывается как переход от начального этапа
  // к конечному и наоборот, независимо от типа данных.
  if (col.sortCategory === ColumnCategory.Stage) {
    return t(asc ? "sort_tip_stage_asc" : "sort_tip_stage_desc");
  }
  switch (col.dataType) {
    case DT.Number: return t(asc ? "sort_tip_num_asc" : "sort_tip_num_desc");
    case DT.String: return t(asc ? "sort_tip_str_asc" : "sort_tip_str_desc");
    case DT.Boolean: return t(asc ? "sort_tip_bool_asc" : "sort_tip_bool_desc");
    case DT.DateTime: return t(asc ? "sort_tip_date_asc" : "sort_tip_date_desc");
    default: return t(asc ? "sort_tip_gen_asc" : "sort_tip_gen_desc");
  }
}

// Название колонки зажимается в 3 строки, многоточие ставится В НАЧАЛЕ — так видна
// концовка названия, а не его начало.
// Чистым CSS так нельзя: line-clamp умеет «…» только справа, а трюк с direction: rtl
// разворачивает слова. Поэтому замер и обрезка выполняются вручную.
// Название хранится как HTML (бывает <br/>), поэтому режутся не строка, а DOM-узлы —
// иначе порвётся разметка. Полный текст лежит в WeakMap, в самом узле он уже обрезан.
// Высота меряется по контейнеру .qtl-th__col (он блочный), у inline-текста box-метрики
// врут. Первая строка может быть выше из-за значка сортировки, поэтому её высота не
// прикидывается, а замеряется отдельно.
const fullTextHtml = new WeakMap<HTMLElement, string>();
const clampObservers = new WeakMap<HTMLElement, ResizeObserver>();
const clampLastWidth = new WeakMap<HTMLElement, number>();
const clampColId = new WeakMap<HTMLElement, string>();
// какие колонки сейчас реально обрезаны — для них при наведении доступна подсказка
const truncatedCols = reactive<Record<string, boolean>>({});

function removeFirstLeadingChar(textEl: HTMLElement, ellipsis: Text): boolean {
  let node: Node | null = ellipsis.nextSibling;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const tn = node as Text;
      if (tn.data.length > 0) {
        tn.deleteData(0, 1);
        if (tn.data.length === 0) textEl.removeChild(tn);
        return true;
      }
      const empty = node;
      node = node.nextSibling;
      textEl.removeChild(empty);
      continue;
    }
    // в начале целый элемент (тот же <br/>) — удаляется целиком
    textEl.removeChild(node);
    return true;
  }
  return false;
}

function applyLeadClamp(colEl: HTMLElement) {
  const textEl = colEl.querySelector<HTMLElement>(".qtl-th__text");
  if (!textEl) return;

  const lh = parseFloat(getComputedStyle(textEl).lineHeight) || 16;
  // пол-строки запаса на субпиксели: без него третья строка иногда «не влезает» на
  // доли пикселя и текст схлопывается до двух. Меньше полстроки — значит честная
  // четвёртая строка (+lh) за лимит всё равно вылезет и обрежется.
  const tol = Math.floor(lh / 2);

  // лимит сбрасывается, чтобы измерить настоящую высоту
  colEl.style.overflow = "";
  colEl.style.maxHeight = "";

  // высота одной строки: в текст ставится один символ и читается scrollHeight. В него
  // уже зашиты и паддинги колонки, и значок сортировки на первой строке — без догадок
  textEl.innerHTML = "\u00A0";
  const oneLine = colEl.scrollHeight;
  const cap = oneLine + Math.round(lh * (LEAD_CLAMP_MAX_LINES - 1)) + tol;

  // полный текст возвращается, высота прижимается к трём строкам
  textEl.innerHTML = fullTextHtml.get(colEl) ?? "";
  colEl.style.overflow = "hidden";
  colEl.style.maxHeight = `${cap}px`;

  const id = clampColId.get(colEl);

  // всё и так в трёх строках — резать нечего
  if (colEl.scrollHeight <= cap) {
    if (id) truncatedCols[id] = false;
    return;
  }

  const ellipsis = document.createTextNode("…");
  textEl.insertBefore(ellipsis, textEl.firstChild);

  // символы откусываются с начала, пока хвост с «…» не влезет. guard на случай, если
  // из-за округлений условие вдруг не сойдётся — чтобы цикл не завис
  let guard = 100000;
  while (colEl.scrollHeight > cap && guard-- > 0) {
    if (!removeFirstLeadingChar(textEl, ellipsis)) break;
  }

  if (id) truncatedCols[id] = true;
}

const vLeadClamp: Directive<HTMLElement, LeadClampValue> = {
  mounted(el, binding) {
    fullTextHtml.set(el, binding.value?.text ?? "");
    clampColId.set(el, binding.value?.id ?? "");
    clampLastWidth.set(el, Math.round(el.clientWidth));
    applyLeadClamp(el);
    // отслеживается только ширина: от высоты пересчёт не зависит, потому что
    // applyLeadClamp сам временно меняет высоту контейнера — иначе были бы лишние
    // срабатывания и петля
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? el.clientWidth);
      if (clampLastWidth.get(el) === w) return;
      clampLastWidth.set(el, w);
      applyLeadClamp(el);
    });
    ro.observe(el);
    clampObservers.set(el, ro);
  },
  updated(el, binding) {
    const textChanged = binding.value?.text !== binding.oldValue?.text;
    if (textChanged) fullTextHtml.set(el, binding.value?.text ?? "");
    clampColId.set(el, binding.value?.id ?? "");
    const w = Math.round(el.clientWidth);
    // ни текст, ни ширина не изменились — пересчёт не нужен. За шириной всё равно
    // следит ResizeObserver, поэтому посторонние ререндеры компонента пропускаются
    if (!textChanged && clampLastWidth.get(el) === w) return;
    clampLastWidth.set(el, w);
    applyLeadClamp(el);
  },
  beforeUnmount(el) {
    clampObservers.get(el)?.disconnect();
    clampObservers.delete(el);
    fullTextHtml.delete(el);
    clampLastWidth.delete(el);
    const id = clampColId.get(el);
    if (id) delete truncatedCols[id];
    clampColId.delete(el);
  },
};

const props = __props;

const emit = __emit;

function onHeaderClick(col: LiteColumn) {
  // во время обновления таблица заблокирована — сортировку не переключаем
  if (props.blocked) return;
  if (!col.sort) return;
  // порядок цикла зависит от типа колонки (см. nextOrderOf): числовые начинают
  // с убывания, остальные — с возрастания
  const next = nextOrderOf(col);
  emit("sort", { col, nextOrder: next });
}

// общая сортировка по закреплённым — тоже блокируется во время обновления
function onPinnedSortClick() {
  if (props.blocked) return;
  emit("togglePinnedOrder");
}

/*
 Классы для ячейки шапки. Запоминаем, по какой колонке сейчас сортируем,
 чтобы можно было слегка подсветить её фон, как делали раньше.
*/
function thClasses(col: LiteColumn) {
  const sorting = col.order === 2 ? "asc" : col.order === 3 ? "desc" : "none";
  return {
    "qtl-th": true,
    "qtl-th--multiedit": !!col.isMultiedit,
    "qtl-th--sortable": col.sort,
    "qtl-th--sort-active": col.sort && (sorting === "asc" || sorting === "desc"),
  };
}

/*
 Подсказка для обрезанных названий. Появляется через 300мс после наведения на
 ячейку, но только если эта колонка действительно обрезана (truncatedCols). Позиция и
 ширина берутся с самой ячейки, поэтому подсказка ложится ровно поверх неё по верху и
 бокам, а вниз растёт сколько надо.
*/
// колонку в подсказке держим по id и берём живую из props.columns — тогда после
// клика по кнопке сортировки в оверлее стрелка обновляется так же, как в самой шапке
const overlayColId = ref<string | null>(null);
const overlayCol = computed<LiteColumn | null>(
  () => props.columns.find((c) => c.id === overlayColId.value) ?? null,
);
const overlayRect = ref<{ left: number; top: number; width: number } | null>(null);
let showTimer: ReturnType<typeof setTimeout> | undefined;

const overlayStyle = computed(() => {
  const r = overlayRect.value;
  if (!r) return "";
  // на полпикселя левее и на пиксель выше, ширина как у ячейки — чтобы подсказка
  // вставала по границам
  return `left:${r.left - 0.5}px; top:${r.top - 1}px; width:${r.width}px;`;
});

function clearShowTimer() {
  if (showTimer !== undefined) {
    clearTimeout(showTimer);
    showTimer = undefined;
  }
}

function onThEnter(col: LiteColumn, ev: MouseEvent) {
  if (col.isMultiedit || !truncatedCols[col.id]) return;
  const thEl = ev.currentTarget as HTMLElement;
  clearShowTimer();
  showTimer = setTimeout(() => {
    const r = thEl.getBoundingClientRect();
    overlayRect.value = { left: r.left, top: r.top, width: r.width };
    overlayColId.value = col.id;
  }, 300);
}

function onThLeave() {
  // пока подсказка не показалась — её появление отменяется. Если уже показалась,
  // действий нет: курсор ушёл на саму подсказку (она лежит сверху), и она прячется по
  // собственному mouseleave
  clearShowTimer();
}

function hideOverlay() {
  clearShowTimer();
  overlayColId.value = null;
  overlayRect.value = null;
}

// при прокрутке координаты ячейки устаревают, поэтому подсказка убирается
onMounted(() => window.addEventListener("scroll", hideOverlay, true));
onBeforeUnmount(() => {
  window.removeEventListener("scroll", hideOverlay, true);
  clearShowTimer();
});

return (_ctx: any,_cache: any) => {
  const _component_q_tooltip = _resolveComponent("q-tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("thead", {
      class: _normalizeClass(["qtl-thead", { 'qtl-thead--sticky': _ctx.headerMode === 'sticky' }])
    }, [
      _createElementVNode("tr", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.columns, (col) => {
          return (_openBlock(), _createElementBlock("th", {
            key: col.id,
            "data-col-id": col.id,
            class: _normalizeClass(thClasses(col)),
            onMouseenter: ($event: any) => (onThEnter(col, $event)),
            onMouseleave: onThLeave
          }, [
            (col.isMultiedit)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_2, [
                    (_ctx.WA)
                      ? (_openBlock(), _createBlock(MostaCheckbox, {
                          key: 0,
                          class: "qtl-th__check",
                          "model-value": _ctx.headerCheckbox,
                          "true-value": 'all',
                          "false-value": 'none',
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('headerCheckboxChange', $event))),
                          onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                        }, null, 8, ["model-value"]))
                      : _createCommentVNode("", true),
                    _createElementVNode("button", {
                      class: "qtl-th__edit-btn",
                      type: "button",
                      disabled: !_ctx.WA || !_ctx.multieditAllowed,
                      "aria-label": t('multi_edit'),
                      onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (_ctx.$emit('multieditClick', $event)), ["stop"]))
                    }, _cache[4] || (_cache[4] = [
                      _createElementVNode("svg", {
                        width: "14",
                        height: "14",
                        viewBox: "0 0 16 16",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg"
                      }, [
                        _createElementVNode("path", {
                          d: "M14.9372 3.83936C15.1701 3.60633 15.1203 3.1787 14.8259 2.8843L13.1148 1.17324C12.8203 0.878802 12.3927 0.828861 12.1597 1.06191L10.7524 2.46913L13.5299 5.24659L14.9372 3.83936Z",
                          fill: "currentColor",
                          stroke: "currentColor",
                          "stroke-width": "0.390625"
                        }),
                        _createElementVNode("path", {
                          d: "M1.00066 15.195C1.64288 15.0543 3.58866 14.546 4.27003 14.3671C4.42665 14.3261 4.56959 14.2439 4.68397 14.1292L12.8352 5.97791L10.0576 3.20032L1.90638 11.3516C1.79163 11.4659 1.7096 11.6089 1.66846 11.7656C1.4895 12.4472 0.981335 14.3934 0.841554 15.0363C0.831666 15.0807 0.845216 15.1271 0.877441 15.1592C0.909666 15.1914 0.956223 15.2048 1.00066 15.195Z",
                          fill: "currentColor",
                          stroke: "currentColor",
                          "stroke-width": "0.390625"
                        })
                      ], -1)
                    ]), 8, _hoisted_3)
                  ]),
                  (_ctx.showGlobalPinnedSort)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "qtl-th__global-sort-btn",
                        type: "button",
                        "aria-label": t('sort_by_pinned'),
                        disabled: _ctx.blocked,
                        onClick: _withModifiers(onPinnedSortClick, ["stop"])
                      }, [
                        (_openBlock(), _createElementBlock("svg", _hoisted_5, [
                          _createElementVNode("g", {
                            class: _normalizeClass(_ctx.orderByPinned ? 'qtl-sort-arrow--active' : 'qtl-sort-arrow--idle')
                          }, _cache[5] || (_cache[5] = [
                            _createElementVNode("path", {
                              d: "M2 10.6666L4.66667 13.3333L7.33333 10.6666",
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round"
                            }, null, -1),
                            _createElementVNode("path", {
                              d: "M4.66669 13.3333V2.66663",
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round"
                            }, null, -1)
                          ]), 2),
                          _cache[6] || (_cache[6] = _createElementVNode("g", { class: "qtl-sort-arrow--idle" }, [
                            _createElementVNode("path", {
                              d: "M14 5.33329L11.3334 2.66663L8.66669 5.33329",
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round"
                            }),
                            _createElementVNode("path", {
                              d: "M11.3333 2.66663V13.3333",
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round"
                            })
                          ], -1))
                        ]))
                      ], 8, _hoisted_4))
                    : _createCommentVNode("", true)
                ], 64))
              : _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (col.sort)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "qtl-th__sort-btn",
                        type: "button",
                        "aria-label": "Sort",
                        disabled: _ctx.blocked,
                        onClick: _withModifiers(($event: any) => (onHeaderClick(col)), ["stop"])
                      }, [
                        _createVNode(QtLiteSortIcon, {
                          "data-type": col.dataType,
                          order: col.order
                        }, null, 8, ["data-type", "order"]),
                        (!_ctx.blocked)
                          ? (_openBlock(), _createBlock(_component_q_tooltip, {
                              key: 0,
                              delay: 300,
                              anchor: "top middle",
                              self: "center middle",
                              class: "qtl-sort-tooltip"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(sortTip(col)), 1)
                              ]),
                              _: 2
                            }, 1024))
                          : _createCommentVNode("", true)
                      ], 8, _hoisted_7))
                    : _createCommentVNode("", true),
                  _cache[7] || (_cache[7] = _createElementVNode("span", { class: "qtl-th__text" }, null, -1))
                ])), [
                  [vLeadClamp, { id: col.id, text: col.text }]
                ])
          ], 42, _hoisted_1))
        }), 128))
      ])
    ], 2),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "qtl-th__overlay-fade" }, {
        default: _withCtx(() => [
          (overlayCol.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "qtl-th__overlay",
                style: _normalizeStyle(overlayStyle.value),
                onMouseleave: hideOverlay
              }, [
                _createElementVNode("div", _hoisted_8, [
                  (overlayCol.value.sort)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "qtl-th__sort-btn",
                        type: "button",
                        "aria-label": "Sort",
                        disabled: _ctx.blocked,
                        onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (onHeaderClick(overlayCol.value)), ["stop"]))
                      }, [
                        _createVNode(QtLiteSortIcon, {
                          "data-type": overlayCol.value.dataType,
                          order: overlayCol.value.order
                        }, null, 8, ["data-type", "order"]),
                        (!_ctx.blocked)
                          ? (_openBlock(), _createBlock(_component_q_tooltip, {
                              key: 0,
                              delay: 300,
                              anchor: "top middle",
                              self: "center middle",
                              class: "qtl-sort-tooltip"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(sortTip(overlayCol.value)), 1)
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ], 8, _hoisted_9))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    class: "qtl-th__text",
                    innerHTML: overlayCol.value.text
                  }, null, 8, _hoisted_10)
                ])
              ], 36))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ], 64))
}
}

})